import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Copy,
  Edit,
  Trash2,
  Eye,
  Package,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  MessageCircle,
  Image as ImageIcon,
  Tag,
  DollarSign,
  RefreshCw,
  Loader2,
  Ban,
  CheckCircle,
  X,
  Download,
  Sparkles,
  Globe,
  Upload,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import { StarRating } from '../components/ui/star-rating';
import { formatCurrency, formatDate, calculateProductRating, hasPendingReviews, getPendingReviewsCount } from '../lib/utils';
import { usePaginatedProducts } from '../hooks/usePaginatedProducts';
import { useProductStatistics } from '../hooks/useProductStatistics';
import { ProductForm } from '../components/ProductForm';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { ProductDetailsModal } from '../components/ProductDetailsModal';
import { ReviewManagementModal } from '../components/ReviewManagementModal';
import { CategoryFilter } from '../components/CategoryFilter';
import { BrandFilter } from '../components/BrandFilter';
import ProductSort from '../components/ProductSort';
import {
  fetchProductsPaginated,
  deleteProduct,
  getProductCount,
  searchProducts,
  updateProductReview,
  deleteProductReview,
  batchUpdateProductReviews,
  batchDeleteProductReviews,
  toggleProductDisabled,
  enableAllProducts,
  disableAllProducts,
  removeNewFlagFromAllProducts,
  exportProductsToCSV,
  setRecentProductsToNew,
  exportProductsMissingInternationalShippingInfoToJSON,
  applyInternationalShippingImportUpdates,
  exportEnabledProductsMissingCommodityCodesToJSON,
  applyCommodityCodesImportUpdates,
  normalizeEnabledProductPrices,
} from '../services/productService';
import { Product, Review } from '../types';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { SetNewProductsModal } from '../components/SetNewProductsModal';
import { InternationalShippingImportModal, InternationalShippingImportItem } from '../components/InternationalShippingImportModal';
import { CommodityCodesImportModal, CommodityCodesImportItem } from '../components/CommodityCodesImportModal';

export function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [pendingDisableAction, setPendingDisableAction] = useState<{ product: Product; newStatus: boolean } | null>(null);
  const [showBulkEnableDialog, setShowBulkEnableDialog] = useState(false);
  const [showBulkDisableDialog, setShowBulkDisableDialog] = useState(false);
  const [showBulkRemoveNewDialog, setShowBulkRemoveNewDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showSetNewDialog, setShowSetNewDialog] = useState(false);
  const [showIntlImportDialog, setShowIntlImportDialog] = useState(false);
  const [showCommodityCodesImportDialog, setShowCommodityCodesImportDialog] = useState(false);
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [missingIntlExportLoading, setMissingIntlExportLoading] = useState(false);
  const [missingCommodityCodesExportLoading, setMissingCommodityCodesExportLoading] = useState(false);
  const [intlImportLoading, setIntlImportLoading] = useState(false);
  const [commodityCodesImportLoading, setCommodityCodesImportLoading] = useState(false);
  const [setNewLoading, setSetNewLoading] = useState(false);
  const [cloneSourceProduct, setCloneSourceProduct] = useState<Product | null>(null);
  const [priceNormalizeLoading, setPriceNormalizeLoading] = useState(false);
  
  const {
    products,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    searchQuery,
    selectedCategory,
    selectedBrand,
    selectedAttributes,
    selectedStatuses,
    sortField,
    sortOrder,
    isSearchMode,
    isFilterMode,
    isSortMode,
    isCombinedMode,
    searchLoading,
    filterLoading,
    sortLoading,
    setSelectedCategory,
    setSelectedBrand,
    setSelectedAttributes,
    setSelectedStatuses,
    setSorting,
    handleSearch,
    fetchPage,
    refresh,
    updateProductInCache,
    removeProductFromCache,
    invalidateCache,
  } = usePaginatedProducts({ pageSize: 20 });
  
  const { 
    statistics, 
    percentageChanges,
    previousSnapshot,
    loading: statisticsLoading, 
    error: statisticsError,
    refresh: refreshStatistics 
  } = useProductStatistics();

  // Handle URL search parameters from notifications
  useEffect(() => {
    const searchParam = searchParams.get('search');
    if (searchParam && searchParam !== searchQuery) {
      // Auto-trigger search with the parameter value
      handleSearch(searchParam);
      
      // Clear the URL parameter after triggering search to keep URL clean
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('search');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, searchQuery, handleSearch, setSearchParams]);

  // Extract individual statistics values
  const totalProducts = statistics?.totalProducts ?? 0;
  const lowStockProducts = statistics?.lowStockProducts ?? 0;
  const outOfStockProducts = statistics?.outOfStockProducts ?? 0;
  const totalValue = statistics?.totalInventoryValue ?? 0;

  // Helper function to format percentage changes
  const formatPercentageChange = (percentage: number) => {
    const isPositive = percentage >= 0;
    const formattedPercentage = Math.abs(percentage).toFixed(1);
    return {
      text: `${isPositive ? '+' : '-'}${formattedPercentage}% from last month`,
      className: isPositive ? 'text-green-600' : 'text-red-600',
      iconClassName: isPositive ? 'text-green-500' : 'text-red-500',
      Icon: isPositive ? TrendingUp : TrendingDown
    };
  };

  const getStockStatus = (product: any) => {
    if (!product.stockQuantity || product.stockQuantity === 0) {
      return { status: 'Out of Stock', color: 'bg-red-100 text-red-800' };
    }
    if (product.stockQuantity <= (product.lowStockThreshold || 10)) {
      return { status: 'Low Stock', color: 'bg-yellow-100 text-yellow-800' };
    }
    return { status: 'In Stock', color: 'bg-green-100 text-green-800' };
  };

  // Handle refresh for both products and statistics
  const handleRefresh = async () => {
    try {
      await Promise.all([refresh(), refreshStatistics()]);
      toast.success('Data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh data. Please try again.');
    }
  };



  // Modal handlers
  const handleAddProduct = () => {
    setFormMode('add');
    setSelectedProduct(null);
    setCloneSourceProduct(null);
    setShowProductForm(true);
  };

  const handleEditProduct = (product: Product) => {
    setFormMode('edit');
    setSelectedProduct(product);
    setCloneSourceProduct(null);
    setShowProductForm(true);
  };

  const handleCloneProduct = (product: Product) => {
    setFormMode('add');
    setSelectedProduct(null);
    setCloneSourceProduct(product);
    setShowProductForm(true);
  };

  const handleDeleteProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowDeleteDialog(true);
  };

  const handleViewDetails = (product: Product) => {
    setSelectedProduct(product);
    setShowDetailsModal(true);
  };

  const handleManageReviews = (product: Product) => {
    setSelectedProduct(product);
    setShowReviewModal(true);
  };

  const handleCloneFromDetails = (product: Product) => {
    setShowDetailsModal(false);
    handleCloneProduct(product);
  };

  const handleToggleDisabled = async (product: Product) => {
    const newDisabledStatus = !product.disabled;
    setPendingDisableAction({ product, newStatus: newDisabledStatus });
    setShowDisableDialog(true);
  };
  
  const handleConfirmDisable = async () => {
    if (!pendingDisableAction) return;

    try {
      setDisableLoading(true);
      const { product, newStatus } = pendingDisableAction;
      
      await toggleProductDisabled(product.id, newStatus);
      
      const actionText = newStatus ? 'disabled' : 'enabled';
      toast.success(`Product ${actionText} successfully`);

      if (isSearchMode || isFilterMode || isSortMode || isCombinedMode) {
        // Update cache with new disabled status and refresh statistics
        const updatedProduct = { ...product, disabled: newStatus };
        updateProductInCache(updatedProduct);
        await refreshStatistics();
      } else {
        // For browse mode, do a full refresh (which includes statistics)
        await handleRefresh();
      }
      
      setShowDisableDialog(false);
      setPendingDisableAction(null);
    } catch (error) {
      console.error('Error toggling product status:', error);
      toast.error('Failed to update product status. Please try again.');
    } finally {
      setDisableLoading(false);
    }
  };

  const handleFormSuccess = (updatedProduct?: Product) => {
    // Show success toast
    if (formMode === 'add') {
      toast.success('Product created successfully');
    } else {
      toast.success('Product updated successfully');
    }
    
    if (updatedProduct && formMode === 'edit' && (isSearchMode || isFilterMode || isSortMode || isCombinedMode)) {
      // Use cache updates when in any special mode
      updateProductInCache(updatedProduct);
      // Always refresh statistics when product data changes
      refreshStatistics();
    } else {
      // For browse mode or new products, always do a full refresh
      if (isSearchMode || isFilterMode || isSortMode || isCombinedMode) {
        invalidateCache();
      }
      handleRefresh();
    }
    setShowProductForm(false);
    setSelectedProduct(null);
    setCloneSourceProduct(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedProduct) return;

    try {
      setDeleteLoading(true);
      await deleteProduct(selectedProduct.id);
      
      // Show success toast
      toast.success('Product deleted successfully');
      
      if (isSearchMode || isFilterMode || isSortMode || isCombinedMode) {
        // Use cache removal when in any special mode and refresh statistics
        removeProductFromCache(selectedProduct.id);
        await refreshStatistics();
      } else {
        // For browse mode, do a full refresh (which includes statistics)
        await handleRefresh();
      }
      
      setShowDeleteDialog(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkEnableProducts = () => {
    setShowBulkEnableDialog(true);
  };

  const handleBulkDisableProducts = () => {
    setShowBulkDisableDialog(true);
  };

  const handleConfirmBulkEnable = async () => {
    try {
      setBulkOperationLoading(true);
      const result = await enableAllProducts();
      
      if (result.updated > 0) {
        toast.success(`Successfully enabled ${result.updated} products`);
        // Refresh both products and statistics
        await handleRefresh();
      } else {
        toast.info('No disabled products found to enable');
      }
      
      setShowBulkEnableDialog(false);
    } catch (error) {
      console.error('Error enabling all products:', error);
      toast.error('Failed to enable all products. Please try again.');
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleConfirmBulkDisable = async () => {
    try {
      setBulkOperationLoading(true);
      const result = await disableAllProducts();
      
      if (result.updated > 0) {
        toast.success(`Successfully disabled ${result.updated} products`);
        // Refresh both products and statistics
        await handleRefresh();
      } else {
        toast.info('No enabled products found to disable');
      }
      
      setShowBulkDisableDialog(false);
    } catch (error) {
      console.error('Error disabling all products:', error);
      toast.error('Failed to disable all products. Please try again.');
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleBulkRemoveNewFlag = () => {
    setShowBulkRemoveNewDialog(true);
  };

  const handleConfirmBulkRemoveNew = async () => {
    try {
      setBulkOperationLoading(true);
      const result = await removeNewFlagFromAllProducts();
      
      if (result.updated > 0) {
        toast.success(`Successfully removed "new" flag from ${result.updated} products`);
        // Refresh both products and statistics
        await handleRefresh();
      } else {
        toast.info('No products with "new" flag found');
      }
      
      setShowBulkRemoveNewDialog(false);
    } catch (error) {
      console.error('Error removing new flags from products:', error);
      toast.error('Failed to remove new flags from products. Please try again.');
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleExportProducts = () => {
    setShowExportDialog(true);
  };

  const handleConfirmExport = async () => {
    try {
      setExportLoading(true);
      await exportProductsToCSV();
      
      toast.success('Products exported to CSV successfully');
      setShowExportDialog(false);
    } catch (error) {
      console.error('Error exporting products:', error);
      toast.error('Failed to export products. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportMissingInternationalShippingJSON = async () => {
    try {
      setMissingIntlExportLoading(true);
      const result = await exportProductsMissingInternationalShippingInfoToJSON();
      if (result.exported === 0) {
        toast.info('No products found missing international shipping/customs info');
      } else {
        toast.success(`Downloaded JSON for ${result.exported} products (max 50)`);
      }
    } catch (error) {
      console.error('Error exporting products missing international shipping/customs info:', error);
      toast.error('Failed to export missing international shipping/customs info. Please try again.');
    } finally {
      setMissingIntlExportLoading(false);
    }
  };

  const handleExportEnabledMissingCommodityCodesJSON = async () => {
    try {
      setMissingCommodityCodesExportLoading(true);
      const result = await exportEnabledProductsMissingCommodityCodesToJSON();
      if (result.exported === 0) {
        toast.info('No enabled products found missing commodity codes');
      } else {
        toast.success(`Downloaded JSON for ${result.exported} products (max 50)`);
      }
    } catch (error) {
      console.error('Error exporting enabled products missing commodity codes:', error);
      toast.error('Failed to export enabled products missing commodity codes. Please try again.');
    } finally {
      setMissingCommodityCodesExportLoading(false);
    }
  };

  const handleOpenIntlImport = () => {
    setShowIntlImportDialog(true);
  };

  const handleOpenCommodityCodesImport = () => {
    setShowCommodityCodesImportDialog(true);
  };

  const handleNormalizeEnabledPrices = async () => {
    try {
      setPriceNormalizeLoading(true);
      const result = await normalizeEnabledProductPrices();
      if (result.updated === 0) {
        toast.info('All enabled product prices are already rounded.');
      } else {
        toast.success(`Rounded prices on ${result.updated} product(s). Processed: ${result.processed}, Errors: ${result.errors}`);
      }
      await handleRefresh();
    } catch (error) {
      console.error('Error normalizing enabled product prices:', error);
      toast.error('Failed to normalize product prices. Please try again.');
    } finally {
      setPriceNormalizeLoading(false);
    }
  };

  const handleConfirmIntlImport = async (items: InternationalShippingImportItem[]) => {
    try {
      setIntlImportLoading(true);
      const result = await applyInternationalShippingImportUpdates(items);

      if (result.updated === 0 && result.failed === 0) {
        toast.info('No updates applied');
      } else if (result.failed > 0) {
        toast.error(`Updated ${result.updated}. Failed ${result.failed} (see console for details).`);
        console.error('International shipping import failures:', result.failures);
      } else {
        toast.success(`Updated ${result.updated} products`);
      }

      await handleRefresh();
      setShowIntlImportDialog(false);
    } catch (error) {
      console.error('Error importing international shipping/customs info:', error);
      toast.error('Failed to import international shipping/customs info. Please try again.');
    } finally {
      setIntlImportLoading(false);
    }
  };

  const handleConfirmCommodityCodesImport = async (items: CommodityCodesImportItem[]) => {
    try {
      setCommodityCodesImportLoading(true);
      const result = await applyCommodityCodesImportUpdates(items);

      if (result.updated === 0 && result.failed === 0) {
        toast.info('No updates applied');
      } else if (result.failed > 0) {
        toast.error(`Updated ${result.updated}. Failed ${result.failed} (see console for details).`);
        console.error('Commodity codes import failures:', result.failures);
      } else {
        toast.success(`Updated ${result.updated} products`);
      }

      await handleRefresh();
      setShowCommodityCodesImportDialog(false);
    } catch (error) {
      console.error('Error importing commodity codes:', error);
      toast.error('Failed to import commodity codes. Please try again.');
    } finally {
      setCommodityCodesImportLoading(false);
    }
  };

  const handleSetProductsToNew = () => {
    setShowSetNewDialog(true);
  };

  const handleConfirmSetNew = async (count: number) => {
    try {
      setSetNewLoading(true);
      const result = await setRecentProductsToNew(count);
      
      if (result.updated > 0) {
        toast.success(`Successfully marked ${result.updated} products as new`);
        // Refresh both products and statistics
        await handleRefresh();
      } else {
        toast.info('No eligible products found to mark as new');
      }
      
      setShowSetNewDialog(false);
    } catch (error) {
      console.error('Error setting products to new:', error);
      toast.error('Failed to set products to new. Please try again.');
    } finally {
      setSetNewLoading(false);
    }
  };

  // Handle review updates
  const handleReviewsUpdated = (productId: string, updatedReviews: Review[]) => {
    try {
      if (isSearchMode || isFilterMode || isSortMode || isCombinedMode) {
        // Update the product in cache with new reviews when in any special mode
        const updatedProduct = { 
          ...selectedProduct!, 
          reviews: updatedReviews 
        };
        updateProductInCache(updatedProduct);
        setSelectedProduct(updatedProduct);
        // Reviews don't typically affect statistics, but refresh to be safe
        refreshStatistics();
      } else {
        // For browse mode, trigger a refresh (which includes statistics)
        handleRefresh();
      }
    } catch (error) {
      console.error('Error updating reviews:', error);
      toast.error('Failed to update reviews. Please refresh the page.');
    }
  };

  // Auto-scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Show error toasts when errors occur
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Show error toasts for statistics errors
  useEffect(() => {
    if (statisticsError) {
      toast.error('Failed to load product statistics');
    }
  }, [statisticsError]);

  // Handle page change with auto-scroll
  const handlePageChange = (page: number) => {
    fetchPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex justify-between items-center"
      >
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <MoreHorizontal className="w-4 h-4" />
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <DropdownMenuLabel>Bulk</DropdownMenuLabel>
              <DropdownMenuItem
                disabled={loading || bulkOperationLoading}
                onSelect={() => handleBulkEnableProducts()}
                className="flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4 text-green-700" />
                Enable All
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={loading || bulkOperationLoading}
                onSelect={() => handleBulkDisableProducts()}
                className="flex items-center gap-2"
              >
                <Ban className="w-4 h-4 text-orange-700" />
                Disable All
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={loading || bulkOperationLoading}
                onSelect={() => handleBulkRemoveNewFlag()}
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4 text-red-700" />
                Bulk Remove New Flags
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={loading || setNewLoading}
                onSelect={() => handleSetProductsToNew()}
                className="flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4 text-jamaica-green" />
                Set Products to New
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={loading || priceNormalizeLoading}
                onSelect={() => handleNormalizeEnabledPrices()}
                className="flex items-center gap-2"
              >
                {priceNormalizeLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                ) : (
                  <DollarSign className="w-4 h-4 text-jamaica-green" />
                )}
                Round Enabled Prices (2dp)
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <DropdownMenuLabel>Export</DropdownMenuLabel>
              <DropdownMenuItem
                disabled={loading || exportLoading}
                onSelect={() => handleExportProducts()}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4 text-blue-700" />
                Export to CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={loading || missingCommodityCodesExportLoading}
                onSelect={() => handleExportEnabledMissingCommodityCodesJSON()}
                className="flex items-center gap-2"
              >
                {missingCommodityCodesExportLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-700" />
                ) : (
                  <Download className="w-4 h-4 text-blue-700" />
                )}
                Enabled Missing Commodity Codes (JSON)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={loading || missingIntlExportLoading}
                onSelect={() => handleExportMissingInternationalShippingJSON()}
                className="flex items-center gap-2"
              >
                {missingIntlExportLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-700" />
                ) : (
                  <Globe className="w-4 h-4 text-blue-700" />
                )}
                Missing Intl Info (JSON)
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <DropdownMenuLabel>Import</DropdownMenuLabel>
              <DropdownMenuItem
                disabled={loading || intlImportLoading}
                onSelect={() => handleOpenIntlImport()}
                className="flex items-center gap-2"
              >
                {intlImportLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-700" />
                ) : (
                  <Upload className="w-4 h-4 text-blue-700" />
                )}
                Import Intl Info (JSON)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={loading || commodityCodesImportLoading}
                onSelect={() => handleOpenCommodityCodesImport()}
                className="flex items-center gap-2"
              >
                {commodityCodesImportLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-700" />
                ) : (
                  <Upload className="w-4 h-4 text-blue-700" />
                )}
                Import Commodity Codes (JSON)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <Button 
          onClick={handleAddProduct}
          className="bg-jamaica-green hover:bg-jamaica-green/90 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Products</p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : (
                  <p className="text-2xl font-bold text-gray-900">{totalProducts}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                <Package className="h-6 w-6 text-jamaica-green" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              {statisticsLoading ? (
                <span className="text-gray-500">Loading trend...</span>
              ) : percentageChanges && previousSnapshot ? (
                <>
                  {(() => {
                    const trend = formatPercentageChange(percentageChanges.totalProducts);
                    return (
                      <>
                        <trend.Icon className={`h-4 w-4 ${trend.iconClassName} mr-1`} />
                        <span className={trend.className}>{trend.text}</span>
                      </>
                    );
                  })()}
                </>
              ) : (
                <span className="text-gray-500">No historical data</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Low Stock</p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-yellow-600" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : (
                  <p className="text-2xl font-bold text-yellow-600">{lowStockProducts}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              <span className="text-gray-600">Requires attention</span>
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Out of Stock</p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-red-600" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : (
                  <p className="text-2xl font-bold text-red-600">{outOfStockProducts}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                <Package className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
              <span className="text-red-600">Needs restocking</span>
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Inventory Value</p>
                {statisticsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-jamaica-yellow" />
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : statisticsError ? (
                  <p className="text-2xl font-bold text-red-600">Error</p>
                ) : (
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalValue)}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-jamaica-yellow/20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-jamaica-yellow" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm">
              {statisticsLoading ? (
                <span className="text-gray-500">Loading trend...</span>
              ) : percentageChanges && previousSnapshot ? (
                <>
                  {(() => {
                    const trend = formatPercentageChange(percentageChanges.totalInventoryValue);
                    return (
                      <>
                        <trend.Icon className={`h-4 w-4 ${trend.iconClassName} mr-1`} />
                        <span className={trend.className}>{trend.text}</span>
                      </>
                    );
                  })()}
                </>
              ) : (
                <span className="text-gray-500">No historical data</span>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 bg-gray-50 border-0 focus:bg-white focus:ring-2 focus:ring-jamaica-green/20"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <ProductSort
              sortField={sortField}
              sortOrder={sortOrder}
              onSortChange={setSorting}
            />
            
            <Button
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
              {(selectedAttributes.length > 0 || selectedStatuses.length > 0 || selectedCategory !== 'All' || selectedBrand !== 'All') && (
                <Badge className={`ml-1 border-transparent ${showFilters ? 'bg-white text-jamaica-green hover:bg-white hover:text-jamaica-green' : 'bg-jamaica-green text-white hover:bg-jamaica-green hover:text-white'}`}>
                  {(selectedCategory !== 'All' ? 1 : 0) + (selectedBrand !== 'All' ? 1 : 0) + selectedAttributes.length + selectedStatuses.length}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-4 pt-4 border-t border-gray-200 space-y-4"
          >
            {/* Attribute Filters */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium text-gray-700 mr-2">Attributes:</span>
              {[
                { key: 'new', label: 'New', color: 'bg-jamaica-green text-white', hoverColor: 'hover:bg-jamaica-green hover:text-white' },
                { key: 'best-seller', label: 'Best Seller', color: 'bg-jamaica-yellow text-jamaica-black', hoverColor: 'hover:bg-jamaica-yellow hover:text-jamaica-black' },
                { key: 'on-sale', label: 'On Sale', color: 'bg-jamaica-black text-jamaica-yellow', hoverColor: 'hover:bg-jamaica-black hover:text-jamaica-yellow' }
              ].map((attr) => (
                <Button
                  key={attr.key}
                  variant={selectedAttributes.includes(attr.key) ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (selectedAttributes.includes(attr.key)) {
                      setSelectedAttributes(selectedAttributes.filter(a => a !== attr.key));
                    } else {
                      setSelectedAttributes([...selectedAttributes, attr.key]);
                    }
                  }}
                  className={selectedAttributes.includes(attr.key) ? `${attr.color} ${attr.hoverColor}` : ""}
                >
                  {attr.label}
                </Button>
              ))}
            </div>

            {/* Status Filters */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium text-gray-700 mr-2">Status:</span>
              {[
                { key: 'in-stock', label: 'In Stock', color: 'bg-green-100 text-green-800', hoverColor: 'hover:bg-green-100 hover:text-green-800' },
                { key: 'low-stock', label: 'Low Stock', color: 'bg-yellow-100 text-yellow-800', hoverColor: 'hover:bg-yellow-100 hover:text-yellow-800' },
                { key: 'out-of-stock', label: 'Out of Stock', color: 'bg-red-100 text-red-800', hoverColor: 'hover:bg-red-100 hover:text-red-800' },
                { key: 'pending-reviews', label: 'Pending Reviews', color: 'bg-purple-100 text-purple-800', hoverColor: 'hover:bg-purple-100 hover:text-purple-800' },
                { key: 'enabled', label: 'Enabled', color: 'bg-blue-100 text-blue-800', hoverColor: 'hover:bg-blue-100 hover:text-blue-800' },
                { key: 'disabled', label: 'Disabled', color: 'bg-gray-100 text-gray-800', hoverColor: 'hover:bg-gray-100 hover:text-gray-800' }
              ].map((status) => (
                <Button
                  key={status.key}
                  variant={selectedStatuses.includes(status.key) ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (selectedStatuses.includes(status.key)) {
                      setSelectedStatuses(selectedStatuses.filter(s => s !== status.key));
                    } else {
                      setSelectedStatuses([...selectedStatuses, status.key]);
                    }
                  }}
                  className={selectedStatuses.includes(status.key) ? `${status.color} ${status.hoverColor}` : ""}
                >
                  {status.label}
                </Button>
              ))}
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Category:</span>
              <CategoryFilter
                value={selectedCategory}
                onChange={setSelectedCategory}
                className="w-48"
                zIndex={1000}
              />
            </div>

            {/* Brand Filter */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">Brand:</span>
              <BrandFilter
                value={selectedBrand}
                onChange={setSelectedBrand}
                className="w-48"
                zIndex={999}
              />
            </div>

            {/* Clear Filters */}
            {(selectedAttributes.length > 0 || selectedStatuses.length > 0 || selectedCategory !== 'All' || selectedBrand !== 'All' || sortField) && (
              <div className="pt-2 border-t border-gray-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedCategory('All');
                    setSelectedBrand('All');
                    setSelectedAttributes([]);
                    setSelectedStatuses([]);
                    setSorting(null, 'desc');
                  }}
                  className="text-jamaica-green hover:text-jamaica-green/90 hover:bg-jamaica-green/10"
                >
                  Clear All Filters & Sorting
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>

      {/* Products Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <Card className="admin-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-jamaica-green" />
              Products ({totalCount})
              {isCombinedMode && (
                <Badge variant="outline" className="ml-2 text-jamaica-black border-jamaica-black hover:bg-transparent hover:text-jamaica-black hover:border-jamaica-black">
                  Combined Mode
                </Badge>
              )}
              {isSearchMode && !isCombinedMode && (
                <Badge variant="outline" className="ml-2 text-jamaica-green border-jamaica-green hover:bg-transparent hover:text-jamaica-green hover:border-jamaica-green">
                  Search Mode
                </Badge>
              )}
              {isFilterMode && !isCombinedMode && (
                <Badge variant="outline" className="ml-2 text-jamaica-yellow border-jamaica-yellow hover:bg-transparent hover:text-jamaica-yellow hover:border-jamaica-yellow">
                  Filter Mode
                </Badge>
              )}
              {isSortMode && !isCombinedMode && (
                <Badge variant="outline" className="ml-2 text-blue-600 border-blue-600 hover:bg-transparent hover:text-blue-600 hover:border-blue-600">
                  Sort Mode
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {error ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Products</h3>
                <p className="text-gray-600 mb-4">{error}</p>
                <Button onClick={handleRefresh} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            ) : loading || searchLoading || filterLoading || sortLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
                <span className="ml-2 text-gray-600">
                  {searchLoading ? 'Searching products...' : 
                   filterLoading ? 'Filtering products...' : 
                   sortLoading ? 'Sorting products...' :
                   'Loading products...'}
                </span>
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products Found</h3>
                <p className="text-gray-600 mb-4">
                  {searchQuery ? 'No products match your search criteria.' : 'No products have been added yet.'}
                </p>
                {searchQuery && (
                  <Button onClick={() => handleSearch('')} variant="outline">
                    Clear Search
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 relative z-10">
                        <TableHead className="w-16"></TableHead>
                        <TableHead className="w-80 max-w-80">Product</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Brand</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead className="w-24">Stock</TableHead>
                        <TableHead className="w-28">Status</TableHead>
                        <TableHead>Sales</TableHead>
                        <TableHead>Rating</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map((product, index) => {
                        const stockStatus = getStockStatus(product);
                        return (
                          <motion.tr
                            key={product.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className={`hover:bg-gray-50 transition-all duration-300 ${
                              product.disabled 
                                ? 'opacity-50 bg-gradient-to-r from-gray-50 to-gray-100 border-l-4 border-l-gray-400 relative' 
                                : 'hover:shadow-sm'
                            }`}
                          >
                            <TableCell>
                              <div className={`w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden transition-all duration-300 ${
                                product.disabled ? 'grayscale brightness-75 contrast-50' : ''
                              }`}>
                                {product.image && product.image !== '/api/placeholder/300/300' ? (
                                  <img 
                                    src={product.image} 
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                      target.nextElementSibling?.classList.remove('hidden');
                                    }}
                                  />
                                ) : null}
                                <ImageIcon className="w-6 h-6 text-gray-400" />
                              </div>
                            </TableCell>
                            <TableCell className="max-w-80">
                              <div className="space-y-1">
                                <div className="flex items-start gap-2">
                                  <p className={`font-medium flex-1 min-w-0 transition-all duration-300 ${
                                    product.disabled 
                                      ? 'text-gray-400 line-through decoration-2 decoration-gray-400' 
                                      : 'text-gray-900'
                                  }`}>{product.name}</p>
                                  <div className="flex flex-wrap gap-1 shrink-0">
                                    {product.isNew && !product.disabled && (
                                      <Badge className="bg-jamaica-green text-white text-[10px] px-1.5 py-0.5 h-5 leading-none hover:bg-jamaica-green hover:text-white border-transparent">NEW</Badge>
                                    )}
                                    {product.isBestSeller && !product.disabled && (
                                      <Badge className="bg-jamaica-yellow text-jamaica-black text-[10px] px-1.5 py-0.5 h-5 leading-none hover:bg-jamaica-yellow hover:text-jamaica-black border-transparent">BEST SELLER</Badge>
                                    )}
                                    {product.onSale && !product.disabled && (
                                      <Badge className="bg-jamaica-black text-jamaica-yellow text-[10px] px-1.5 py-0.5 h-5 leading-none hover:bg-jamaica-black hover:text-jamaica-yellow border-transparent">ON SALE</Badge>
                                    )}
                                  </div>
                                </div>
                                <p className={`text-sm line-clamp-1 pr-2 transition-all duration-300 ${
                                  product.disabled ? 'text-gray-300' : 'text-gray-500'
                                }`}>{product.description}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-[10px] px-1.5 py-0.5 h-5 leading-none transition-all duration-300 ${
                                product.disabled 
                                  ? 'border-gray-300 text-gray-300 bg-gray-50 hover:bg-gray-50 hover:text-gray-300 hover:border-gray-300' 
                                  : 'hover:bg-transparent hover:text-current hover:border-current'
                              }`}>
                                <Tag className="w-3 h-3 mr-1" />
                                {product.category || 'Uncategorized'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {product.brand ? (
                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0.5 h-5 leading-none transition-all duration-300 bg-blue-50 text-blue-700 border-blue-200 ${
                                  product.disabled 
                                    ? 'opacity-50 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200' 
                                    : 'hover:bg-blue-100 hover:text-blue-800 hover:border-blue-300'
                                }`}>
                                  <Package className="w-3 h-3 mr-1" />
                                  {product.brand}
                                </Badge>
                              ) : (
                                <span className={`text-[10px] px-1.5 py-0.5 h-5 leading-none transition-all duration-300 ${
                                  product.disabled ? 'text-gray-300' : 'text-gray-400'
                                }`}>
                                  No brand
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {product.price === 0 ? (
                                  <div className="flex items-center gap-2">
                                    <Badge className="bg-green-100 text-green-800 border-green-200">
                                      FREE
                                    </Badge>
                                    <p className={`text-sm transition-all duration-300 ${
                                      product.disabled ? 'text-gray-400' : 'text-gray-600'
                                    }`}>$0.00</p>
                                  </div>
                                ) : product.onSale && product.onSalePrice ? (
                                  <>
                                    <p className={`font-semibold transition-all duration-300 ${
                                      product.disabled ? 'text-gray-300' : 'text-jamaica-green'
                                    }`}>{formatCurrency(product.onSalePrice)}</p>
                                    <p className={`text-sm line-through transition-all duration-300 ${
                                      product.disabled ? 'text-gray-200' : 'text-gray-500'
                                    }`}>{formatCurrency(product.price)}</p>
                                  </>
                                ) : (
                                  <p className={`font-semibold transition-all duration-300 ${
                                    product.disabled ? 'text-gray-300' : 'text-gray-900'
                                  }`}>{formatCurrency(product.price)}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <p className={`font-medium transition-all duration-300 ${
                                  product.disabled ? 'text-gray-300' : 'text-gray-900'
                                }`}>{product.stockQuantity || 0}</p>
                                <p className={`text-xs transition-all duration-300 ${
                                  product.disabled ? 'text-gray-200' : 'text-gray-500'
                                }`}>Min: {product.lowStockThreshold || 10}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-[10px] px-1.5 py-0.5 h-5 leading-none border-transparent transition-all duration-300 ${
                                product.disabled 
                                  ? 'bg-gray-200 text-gray-400 hover:bg-gray-200 hover:text-gray-400'
                                  : stockStatus.status === 'Out of Stock' ? 'hover:bg-red-100 hover:text-red-800' :
                                    stockStatus.status === 'Low Stock' ? 'hover:bg-yellow-100 hover:text-yellow-800' :
                                    'hover:bg-green-100 hover:text-green-800'
                              } ${product.disabled ? '' : stockStatus.color}`}>
                                {stockStatus.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <p className={`font-medium transition-all duration-300 ${
                                product.disabled ? 'text-gray-300' : 'text-gray-900'
                              }`}>{product.salesCount || 0}</p>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const ratingData = calculateProductRating(product.reviews);
                                const pendingCount = getPendingReviewsCount(product.reviews);
                                return (
                                  <div className="space-y-1">
                                    <div className={`flex items-center gap-1 transition-all duration-300 ${
                                      product.disabled ? 'opacity-40' : ''
                                    }`}>
                                      <StarRating 
                                        rating={ratingData.averageRating} 
                                        size="sm"
                                        showRating={true}
                                      />
                                      <span className={`text-xs transition-all duration-300 ${
                                        product.disabled ? 'text-gray-200' : 'text-gray-500'
                                      }`}>
                                        ({ratingData.approvedReviews})
                                      </span>
                                    </div>
                                    {pendingCount > 0 && !product.disabled && (
                                      <div className="flex items-center gap-1">
                                        <Badge 
                                          variant="outline" 
                                          className="text-[10px] px-1.5 py-0.5 h-5 leading-none bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200 cursor-pointer"
                                          onClick={() => handleManageReviews(product)}
                                        >
                                          {pendingCount} pending
                                        </Badge>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleManageReviews(product)}
                                          className="h-5 w-5 p-0 hover:bg-purple-100"
                                          title="Manage Reviews"
                                        >
                                          <MessageCircle className="w-3 h-3 text-purple-600" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <p className={`text-sm transition-all duration-300 ${
                                product.disabled ? 'text-gray-300' : 'text-gray-600'
                              }`}>
                                {product.updatedAt ? formatDate(new Date(product.updatedAt)) : 'N/A'}
                              </p>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleViewDetails(product)}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleCloneProduct(product)}>
                                    <Copy className="w-4 h-4 mr-2" />
                                    Clone Product
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleEditProduct(product)}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit Product
                                  </DropdownMenuItem>
                                  {product.reviews && product.reviews.length > 0 && (
                                    <DropdownMenuItem onClick={() => handleManageReviews(product)}>
                                      <MessageCircle className="w-4 h-4 mr-2" />
                                      Manage Reviews
                                      {getPendingReviewsCount(product.reviews) > 0 && (
                                        <Badge 
                                          variant="outline" 
                                          className="ml-2 bg-yellow-50 text-yellow-700 border-yellow-200 text-xs hover:bg-yellow-50 hover:text-yellow-700 hover:border-yellow-200"
                                        >
                                          {getPendingReviewsCount(product.reviews)}
                                        </Badge>
                                      )}
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem 
                                    onClick={() => handleToggleDisabled(product)}
                                    className={product.disabled ? "text-green-600" : "text-orange-600"}
                                  >
                                    {product.disabled ? (
                                      <CheckCircle className="w-4 h-4 mr-2" />
                                    ) : (
                                      <Ban className="w-4 h-4 mr-2" />
                                    )}
                                    {product.disabled ? 'Enable Product' : 'Disable Product'}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteProduct(product)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Product
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </motion.tr>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination */}
                <div className="p-6 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    loading={loading}
                    totalItems={totalCount}
                    itemsPerPage={20}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Product Form Modal */}
      <ProductForm
        isOpen={showProductForm}
        onClose={() => {
          setShowProductForm(false);
          setCloneSourceProduct(null);
        }}
        onSuccess={handleFormSuccess}
        product={selectedProduct}
        mode={formMode}
        cloneSource={cloneSourceProduct}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        title="Delete Product"
        description="Are you sure you want to delete this product?"
        itemName={selectedProduct?.name}
      />

      {/* Disable/Enable Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDisableDialog}
        onClose={() => {
          setShowDisableDialog(false);
          setPendingDisableAction(null);
        }}
        onConfirm={handleConfirmDisable}
        loading={disableLoading}
        title={pendingDisableAction?.newStatus ? "Disable Product" : "Enable Product"}
        description={
          pendingDisableAction?.newStatus 
            ? "Are you sure you want to disable this product? It will be hidden from customers." 
            : "Are you sure you want to enable this product? It will be visible to customers."
        }
        itemName={pendingDisableAction?.product?.name}
        confirmText={pendingDisableAction?.newStatus ? "Disable" : "Enable"}
        variant={pendingDisableAction?.newStatus ? "destructive" : "default"}
        type={pendingDisableAction?.newStatus ? "disable" : "enable"}
      />

      {/* Product Details Modal */}
      <ProductDetailsModal
        product={selectedProduct}
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        onEdit={(product) => {
          setShowDetailsModal(false);
          handleEditProduct(product);
        }}
        onClone={handleCloneFromDetails}
      />

      {/* Review Management Modal */}
      <ReviewManagementModal
        product={selectedProduct}
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        onReviewsUpdated={handleReviewsUpdated}
      />

      {/* Bulk Enable All Products Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showBulkEnableDialog}
        onClose={() => setShowBulkEnableDialog(false)}
        onConfirm={handleConfirmBulkEnable}
        loading={bulkOperationLoading}
        title="Enable All Products"
        description="Are you sure you want to enable all currently disabled products? This will make them visible and available for purchase by customers."
        confirmText="Enable All"
        variant="default"
        type="enable"
      />

      {/* Bulk Disable All Products Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showBulkDisableDialog}
        onClose={() => setShowBulkDisableDialog(false)}
        onConfirm={handleConfirmBulkDisable}
        loading={bulkOperationLoading}
        title="Disable All Products"
        description="Are you sure you want to disable all currently enabled products? This will hide them from customers and prevent any purchases. This action affects all products in your store."
        confirmText="Disable All"
        variant="destructive"
        type="disable"
      />

      {/* Bulk Remove New Flags Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showBulkRemoveNewDialog}
        onClose={() => setShowBulkRemoveNewDialog(false)}
        onConfirm={handleConfirmBulkRemoveNew}
        loading={bulkOperationLoading}
        title="Remove New Flags from All Products"
        description="Are you sure you want to remove the 'new' flag from all products that currently have it? This will remove the 'NEW' badge from these products in your store."
        confirmText="Remove New Flags"
        variant="destructive"
        type="delete"
      />

      {/* Export Products Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        onConfirm={handleConfirmExport}
        loading={exportLoading}
        title="Export Enabled Products to CSV"
        description="This will export all enabled products (products visible to customers) to a CSV file with complete product details including inventory, pricing, and metadata. The file will be downloaded to your device."
        confirmText="Export CSV"
        variant="default"
        type="default"
      />

      {/* Set Products to New Modal */}
      <SetNewProductsModal
        isOpen={showSetNewDialog}
        onClose={() => setShowSetNewDialog(false)}
        onConfirm={handleConfirmSetNew}
        loading={setNewLoading}
      />

      <InternationalShippingImportModal
        isOpen={showIntlImportDialog}
        onClose={() => setShowIntlImportDialog(false)}
        onConfirm={handleConfirmIntlImport}
        loading={intlImportLoading}
      />

      <CommodityCodesImportModal
        isOpen={showCommodityCodesImportDialog}
        onClose={() => setShowCommodityCodesImportDialog(false)}
        onConfirm={handleConfirmCommodityCodesImport}
        loading={commodityCodesImportLoading}
      />
    </div>
  );
} 
